import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MarketTicker } from "@/components/MarketTicker";
import { SignalCard } from "@/components/SignalCard";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Zap, Loader2, Crown, Radar } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All Signals" },
  { id: "metals", label: "Gold" },
  { id: "forex", label: "Forex" },
  { id: "crypto", label: "Crypto" },
  { id: "active", label: "Active" },
  { id: "won", label: "Winning History" },
];

const PAIRS = ["XAU/USD", "EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "USD/CAD", "BTC/USD", "ETH/USD", "SOL/USD"];

export default function Dashboard() {
  const { user, openAuth } = useAuth();
  const nav = useNavigate();
  const [signals, setSignals] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [filter, setFilter] = useState("all");
  const [scanPair, setScanPair] = useState("XAU/USD");
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (["metals", "forex", "crypto"].includes(filter)) params.category = filter;
    if (["active", "won"].includes(filter)) params.status = filter;
    const { data } = await api.get("/signals", { params });
    setSignals(data.signals);
    setSubscribed(data.subscribed);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    if (!user) { openAuth("register"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/signals/scan", { pair: scanPair });
      toast.success(`AI generated a ${data.signal} signal on ${data.pair}`);
      await load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg);
      if (e.response?.status === 403) setTimeout(() => nav("/pricing"), 800);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="pt-16">
      <MarketTicker />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">AI Signal Desk</h1>
            <p className="text-slate-400 mt-1">
              Live BUY / SELL signals across Forex, Gold & Crypto.
              {user && !subscribed && (
                <span className="text-amber-400"> You're on the free plan — Gold & Crypto are locked.</span>
              )}
            </p>
          </div>

          {/* Scanner */}
          <div className="qs-card p-4 flex items-center gap-3 w-full lg:w-auto">
            <Radar className="text-cyan-400 shrink-0" size={22} />
            <Select value={scanPair} onValueChange={setScanPair}>
              <SelectTrigger data-testid="scan-pair-select" className="w-36 bg-slate-900/60 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {PAIRS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="run-ai-scanner-button"
              onClick={runScan}
              disabled={scanning}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
            >
              {scanning ? <><Loader2 className="animate-spin mr-2" size={16} /> Scanning…</> : <><Zap size={16} className="mr-2" /> Run AI Scanner</>}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`signal-filter-tab-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f.id
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-800/60 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {!subscribed && (
          <div className="qs-card p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3 border-amber-500/30">
            <span className="flex items-center gap-2 text-sm text-amber-300">
              <Crown size={18} /> Unlock unlimited Gold & Crypto live signals with Pro.
            </span>
            <Button data-testid="dashboard-upgrade-button" onClick={() => nav("/pricing")} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full h-9 px-5">
              View Plans
            </Button>
          </div>
        )}

        {/* Grid */}
        {!signals ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl bg-slate-800/40" />)}
          </div>
        ) : signals.length === 0 ? (
          <div className="text-center py-20 text-slate-500">No signals match this filter yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {signals.map((s) => (
              <SignalCard key={s.id} signal={s} onUpgrade={() => nav("/pricing")} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
